import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, FileType, Loader2 } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { api, API } from "@/lib/api";

const GST = 0.15;
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
const money = (v) => `$${Number(v).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function figure(it, rate) {
  if (it.hours != null && it.hours !== "") {
    const amt = round2(Number(it.hours) * rate);
    const incl = it.incl != null && it.incl !== "" ? Number(it.incl) : round2(amt * (1 + GST));
    return { main: `${Number(it.hours)} ${Number(it.hours) === 1 ? "hr" : "hrs"} × ${money(rate)} = ${money(amt)}`, incl: money(incl) };
  }
  if (it.amount != null && it.amount !== "") {
    const incl = it.incl != null && it.incl !== "" ? Number(it.incl) : round2(Number(it.amount) * (1 + GST));
    return { main: it.per_hour ? `${money(it.amount)} per hour` : money(it.amount), incl: money(incl) };
  }
  return null;
}

export default function SchedulePage() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/charges").then((r) => setC(r.data.charges)); }, []);

  let n = 0;
  const rate = c ? Number(c.hourly_rate) || 0 : 0;

  return (
    <div className="light-scope min-h-screen flex flex-col">
      <Nav theme="light" />
      <div className="flex-1 max-w-[900px] mx-auto w-full px-5 md:px-10 pt-32 md:pt-40 pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#58635C] hover:text-[#0A0D0B] transition-colors mb-8">
          <ArrowLeft size={15} /> Back to home
        </Link>
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#B89874]">Fees</span>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight text-[#0A0D0B] mt-4 leading-[1.05]">Schedule of Charges</h1>

        {!c ? <Loader2 className="animate-spin text-[#B89874] mt-10" /> : (
          <>
            <p className="mt-6 text-[#58635C] leading-relaxed max-w-[70ch]">{c.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`${API}/documents/schedule-of-costs/pdf`} target="_blank" rel="noreferrer" data-testid="schedule-download-pdf"
                className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors">
                <Download size={15} /> Download PDF
              </a>
              <a href={`${API}/documents/schedule-of-costs/docx`} target="_blank" rel="noreferrer" data-testid="schedule-download-docx"
                className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-6 py-3 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors">
                <FileType size={15} /> Word
              </a>
            </div>

            <div className="mt-12 space-y-10" data-testid="schedule-content">
              {c.sections.map((sec, si) => (
                <div key={si}>
                  <h2 className="font-display text-xl md:text-2xl text-[#0A0D0B] border-b border-[#E5E1D8] pb-2">{sec.heading}</h2>
                  {sec.blurb && <p className="mt-3 text-[#58635C]">{sec.blurb}</p>}
                  <div className="mt-4 divide-y divide-[#F0EDE4]">
                    {sec.items.map((it, ii) => {
                      n += 1;
                      const f = figure(it, rate);
                      return (
                        <div key={ii} className="py-3 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                          <div className="flex-1 text-[#233027] text-sm md:text-base">
                            <span className="text-[#B89874] font-mono-gg mr-2">{n}.</span>{it.description}
                            {it.note && <div className="text-xs text-[#8A6D2F] mt-1 font-medium">{it.note}</div>}
                          </div>
                          {f && (
                            <div className="text-right shrink-0">
                              <div className="font-mono-gg text-sm text-[#0A0D0B]">{f.main}</div>
                              <div className="text-xs text-[#58635C]">incl. GST {f.incl}</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {c.footnotes?.length > 0 && (
              <div className="mt-12 border-t border-[#E5E1D8] pt-6 space-y-1.5">
                {c.footnotes.map((fn, i) => (
                  <p key={i} className="text-xs text-[#58635C] leading-relaxed">{fn}</p>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
